import React from 'react';
import BigNumber from 'bignumber.js';
import { formatCurrency, getChangeColorClass } from '../../utils/formatters';
import { getRandomColor } from '../../utils/allocationHelpers';
import Tooltip from '../common/Tooltip';
import CategoryBreakdownTable from './CategoryBreakdownTable';

const percent = (value) => value == null ? '—' : `${new BigNumber(value).toFixed(1)}%`;
const Tip = ({ title, children }) => <Tooltip title={title} position="top" arrow animation="shift" theme="dark" size="small" distance={8}>{children}</Tooltip>;

// Amounts and portfolio weights are server results, never reconstructed from draft rows.
// Decimal operations below only compare progress or format the displayed weight delta.
const PrecomputedCategoryBreakdown = ({ title, categories, currency }) => (
  <CategoryBreakdownTable title={title} currency={currency} hasChangeCols>
    {categories?.length ? categories.map((category, index) => {
      const completed = new BigNumber(category.completedAmount);
      const total = new BigNumber(category.totalAmount);
      const fullyCompleted = completed.gt(0) && completed.gte(total);
      const partial = completed.gt(0) && !fullyCompleted;
      const current = category.currentPercentage;
      const after = category.afterPercentage;
      const known = current != null && after != null;
      const delta = known ? new BigNumber(after).minus(current).decimalPlaces(1) : null;
      const changeTitle = `${percent(current)} -> ${percent(after)}`;
      const allocationTitle = `${percent(category.percentage)} of total allocation`;
      const amount = (value, done = false) => <span className={`${done ? 'text-gray-400 dark:text-gray-500 line-through ' : ''}border-b border-dotted border-current cursor-help whitespace-nowrap`}>{formatCurrency(value, currency)}</span>;
      return (
        <tr key={category.id} className="border-b dark:border-gray-700 text-sm">
          <td className="py-1.5 dark:text-white"><div className="flex items-center"><div className="w-3 h-3 shrink-0 rounded-full mr-2" style={{ backgroundColor: getRandomColor(index + (title === 'Location' ? 5 : 0)) }} />{category.name}</div></td>
          <td className="py-1.5 dark:text-white">
            {partial ? <span className="text-xs"><Tip title={`Completed: ${category.completedAmount} ${currency}`}>{amount(category.completedAmount)}</Tip><span className="mx-1 text-gray-400">/</span><Tip title={allocationTitle}>{amount(category.totalAmount)}</Tip></span> : <Tip title={allocationTitle}>{amount(category.totalAmount, fullyCompleted)}</Tip>}
          </td>
          <td className="py-1.5 dark:text-white">
            {!known ? <Tip title={`Portfolio weight unavailable where shown as —. ${changeTitle}`}><span className="border-b border-dotted border-current cursor-help whitespace-nowrap">{changeTitle}</span></Tip> : delta.isZero() ? <Tip title={changeTitle}><span className="whitespace-nowrap">{percent(current)}</span></Tip> : <Tip title={changeTitle}><span className={`${getChangeColorClass(delta.toNumber())} font-medium border-b border-dotted border-current cursor-help whitespace-nowrap`}>{delta.gt(0) ? '+' : ''}{delta.toFixed(1)}%</span></Tip>}
          </td>
        </tr>
      );
    }) : <tr><td colSpan={3} className="py-3 text-xs text-gray-500 dark:text-gray-400">{categories ? 'No allocations' : 'Breakdown unavailable'}</td></tr>}
  </CategoryBreakdownTable>
);

export default PrecomputedCategoryBreakdown;
